import React, { useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Typography from '../Typography';

const FONT_SIZES = ['10', '11', '12', '13', '14', '15', '16', '18', '20', '22', '24'];
const LINE_WIDTHS = ['1', '1.5', '2', '2.5', '3', '4'];
const PRESET_COLORS = ['#00FF00', '#FFFF00', '#00FFFF', '#FFFFFF', '#FF3B30', '#FF9500', '#AF52DE'];

const CustomSelect = ({ value, options, onChange, unit = 'px' }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          setIsOpen(prev => !prev);
        }}
        className="flex min-w-[100px] items-center justify-between gap-2 rounded border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:border-sky-500 hover:bg-slate-700 focus:border-sky-500 focus:outline-none"
      >
        <span>
          {value} {unit}
        </span>
        <svg
          className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180 text-sky-400' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 max-h-44 w-full min-w-[100px] overflow-y-auto rounded-md border border-slate-600 bg-[#0f172a] py-1 shadow-2xl">
          {options.map(opt => {
            const isSelected = String(opt) === String(value);
            return (
              <button
                key={opt}
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onChange(opt);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs transition-colors ${
                  isSelected
                    ? 'bg-sky-600/30 font-semibold text-sky-300'
                    : 'text-slate-300 hover:bg-sky-600/20 hover:text-white'
                }`}
              >
                <span>
                  {opt} {unit}
                </span>
                {isSelected && <span className="text-sky-400">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AnnotationPreferences = ({ value, onChange }) => {
  const rootRef = useRef(null);
  const scrollParentRef = useRef(null);
  const savedScrollTop = useRef(null);

  // find nearest scrollable ancestor (modal body) once mount
  useEffect(() => {
    let el = rootRef.current?.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      const canScroll = /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
      if (canScroll) {
        scrollParentRef.current = el;
        break;
      }
      el = el.parentElement;
    }
  }, []);

  //  after every render ( before DOM paint ) force scrollTop
  useLayoutEffect(() => {
    if (scrollParentRef.current && savedScrollTop.current !== null) {
      scrollParentRef.current.scrollTop = savedScrollTop.current;
      // restore after one extra frame - if parent gets time then re-render or reflow
      requestAnimationFrame(() => {
        if (scrollParentRef.current && savedScrollTop.current !== null) {
          scrollParentRef.current.scrollTop = savedScrollTop.current;
        }
      });
    }
  });

  const handleFieldChange = (field, fieldValue) => {
    //  before trigger first capture current scroll position
    if (scrollParentRef.current) {
      savedScrollTop.current = scrollParentRef.current.scrollTop;
    }
    onChange({ ...value, [field]: fieldValue });
  };

  return (
    <div
      ref={rootRef}
      className="flex flex-col gap-5 rounded-lg border border-slate-700 bg-slate-900/60 p-4"
      onClick={e => e.stopPropagation()}
    >
      {/* Annotation Line Color */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-3">
        <div>
          <Typography variant="subtitle" className="font-medium text-white">
            Annotation Line Color
          </Typography>
          <span className="text-xs text-slate-400">Color of measurement lines & shapes</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            {PRESET_COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleFieldChange('color', c);
                }}
                className={`h-6 w-6 rounded-full border transition-all ${
                  value.color?.toLowerCase() === c.toLowerCase()
                    ? 'border-white scale-110 shadow-[0_0_8px_rgba(255,255,255,0.6)]'
                    : 'border-transparent opacity-80 hover:opacity-100'
                }`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>
          <input
            type="color"
            value={value.color || '#00FF00'}
            onClick={e => e.stopPropagation()}
            onChange={e => handleFieldChange('color', e.target.value)}
            className="h-7 w-8 cursor-pointer rounded border border-slate-600 bg-slate-800 p-0.5"
            title="Custom Color"
          />
        </div>
      </div>

      {/* Line Width */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-3">
        <div>
          <Typography variant="subtitle" className="font-medium text-white">
            Line Width
          </Typography>
          <span className="text-xs text-slate-400">Thickness of annotation borders</span>
        </div>
        <CustomSelect
          value={value.lineWidth || '1.5'}
          options={LINE_WIDTHS}
          onChange={val => handleFieldChange('lineWidth', val)}
          unit="px"
        />
      </div>

      {/* Text Color */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-3">
        <div>
          <Typography variant="subtitle" className="font-medium text-white">
            Measurement Text Color
          </Typography>
          <span className="text-xs text-slate-400">Color of measurement numbers & text</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            {PRESET_COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleFieldChange('textBoxColor', c);
                }}
                className={`h-6 w-6 rounded-full border transition-all ${
                  value.textBoxColor?.toLowerCase() === c.toLowerCase()
                    ? 'border-white scale-110 shadow-[0_0_8px_rgba(255,255,255,0.6)]'
                    : 'border-transparent opacity-80 hover:opacity-100'
                }`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>
          <input
            type="color"
            value={value.textBoxColor || '#00FF00'}
            onClick={e => e.stopPropagation()}
            onChange={e => handleFieldChange('textBoxColor', e.target.value)}
            className="h-7 w-8 cursor-pointer rounded border border-slate-600 bg-slate-800 p-0.5"
            title="Custom Color"
          />
        </div>
      </div>

      {/* Font Size */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-3">
        <div>
          <Typography variant="subtitle" className="font-medium text-white">
            Font Size
          </Typography>
          <span className="text-xs text-slate-400">Size of measurement text</span>
        </div>
        <CustomSelect
          value={value.textBoxFontSize || '15'}
          options={FONT_SIZES}
          onChange={val => handleFieldChange('textBoxFontSize', val)}
          unit="px"
        />
      </div>

      {/* Live Preview */}
      <div className="rounded-lg border border-slate-700 bg-black/80 p-4 shadow-inner">
        <div className="mb-2 flex items-center justify-between">
          <Typography variant="subtitle" className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Live Preview
          </Typography>
          <span className="text-[11px] text-slate-500">How it looks on viewport</span>
        </div>
        <div className="flex h-16 w-full items-center justify-center rounded bg-[#0a0f18] p-2">
          <svg width="100%" height="50" className="overflow-visible">
            <line
              x1="20"
              y1="25"
              x2="160"
              y2="25"
              stroke={value.color || '#00FF00'}
              strokeWidth={value.lineWidth || '1.5'}
            />
            {/* Start and end ticks */}
            <line
              x1="20"
              y1="18"
              x2="20"
              y2="32"
              stroke={value.color || '#00FF00'}
              strokeWidth={value.lineWidth || '1.5'}
            />
            <line
              x1="160"
              y1="18"
              x2="160"
              y2="32"
              stroke={value.color || '#00FF00'}
              strokeWidth={value.lineWidth || '1.5'}
            />
            <text
              x="175"
              y="30"
              fill={value.textBoxColor || '#00FF00'}
              fontSize={`${value.textBoxFontSize || 15}px`}
              fontFamily="sans-serif"
              fontWeight="500"
            >
              124.8 mm
            </text>
          </svg>
        </div>
      </div>
    </div>
  );
};

AnnotationPreferences.propTypes = {
  value: PropTypes.shape({
    color: PropTypes.string,
    lineWidth: PropTypes.string,
    textBoxColor: PropTypes.string,
    textBoxFontSize: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AnnotationPreferences;